"use client";

import { LATAM_COUNTRIES, PRIVATE_DISCUSSION_BOARD_PATH } from "@/lib/constants";
import { createContext, ReactNode, useContext } from "react";

interface CountryContextType {
	// Basic country info
	name: string;
	code: string;
	country: string[];

	meta?: {
		name: {
			value: string;
			title: string;
			adjective: string;
		};
	};

	// Hero section content
	hero: {
		backgroundImage: string;
		flagImage: string;
		flagAlt: string;
		title: string;
		screenReaderTitle: string;
		description: string;
		welcomeMessage: string;
	};

	// Ambassador section content
	ambassadors: Array<{
		image: string;
		name: string;
		company: string;
		profileLink: string;
	}>;

	// VIPs section content
	vips: {
		title: string;
	};

	// Products section content
	products: {
		perPage: number;
		code?: string;
	};

	// Country stats content
	stats: {
		products: string;
		members: string;
		views: string;
		communities: string;
		courses: string;
	};

	// Country portals content
	portals: {
		current: {
			labelText: string;
			title: string;
			labelTextClassName: string;
			href: string;
			logo: string;
		};
		others: Array<{
			labelText: string;
			title: string;
			labelTextClassName: string;
			href?: string;
			logo: string;
		}>;
	};

	// Featured video content
	featuredVideo: {
		thumbnailUrl: string;
		thumbnailAlt: string;
		videoUrl: string;
		videoTitle: string;
		text: string;
	};

	// Job board content
	jobBoard: {
		title: string;
		jobs: Array<{
			logo: string;
			title: string;
			location: string;
			company: string;
			applyLink: string;
			isRemote?: boolean;
			duration: "Part-Time" | "Full Time";
			lastUpdated?: string;
		}>;
	};

	// Zero state search content
	zeroStateSearch: Array<{
		title: string;
		values: Array<{
			name: string;
			link: string;
		}>;
	}>;

	// Popular Tabs content
	popularTabs?: {
		communities: Array<{
			labelText: string;
			title: string;
			labelTextClassName: string;
			href: string;
			logo: string;
		}>;
		people: Array<{
			name: string;
			username: string;
			jobTitle: string;
			company: string;
			profileImage: string;
			location: string;
			isFounder: boolean;
			isVerified: boolean;
		}>;
		products: Array<{
			name: string;
			logo: string;
			slug: string;
		}>;
	};
}

export const COUNTRY_INDIA_DATA: CountryContextType = {
	name: "India",
	code: "IN",
	country: ["India"],

	meta: {
		name: {
			title: "India",
			value: "india",
			adjective: "Indian",
		},
	},

	hero: {
		backgroundImage: "/images/communities/country-portal/india-header-banner.png",
		flagImage: "/images/communities/country-portal/india-flag.png",
		flagAlt: "India",
		title: "Discover India Proptech",
		screenReaderTitle: "Discover India Proptech Products",
		description:
			"One stop shop to explore proptech technologies in India. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech India",
	},

	ambassadors: [
		{
			image: "/images/communities/country-portal/rohit.webp",
			name: "Rohit Sujan",
			company: "PropTechBuzz",
			profileLink: "https://www.proptechbuzz.com/user/rohit",
		},
	],

	vips: {
		title: "Our Premium VIPs from India",
	},

	products: {
		perPage: 80,
	},

	stats: {
		products: "200+",
		members: "2500+",
		views: "124.4k",
		communities: "1200+",
		courses: "800+",
	},

	portals: {
		current: {
			labelText: "Hot",
			title: "PropTech India",
			labelTextClassName: "bg-red-500",
			href: "/communities/proptech-india/home",
			logo: "/images/communities/country-portal/prop-tech-india.png",
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech UAE",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-uae/home",
				logo: "/images/communities/country-portal/uae-flag.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech Austria",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-austria.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt:
			"Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
		videoUrl: "https://www.youtube-nocookie.com/embed/o9LYjfpyd7E?autoplay=1&start=0&mute=1",
		videoTitle:
			"Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
		text: "Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
	},

	jobBoard: {
		title: "India PropTech Jobs",
		jobs: [
			{
				logo: "https://proptechbuzz-dev.s3.amazonaws.com/others-job-board/accacia.png",
				title: "Head of Growth",
				location: "Bangalore - India, Singapore",
				duration: "Full Time",
				company: "Accacia",
				isRemote: false,
				applyLink:
					"https://dynamic-polka-cd9.notion.site/Careers-07495d488c8e4af5a07d40709f6dadbf?p=e8e0200f29c242d4be70d1f772e99290&pm=c",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://proptechbuzz-dev.s3.amazonaws.com/others-job-board/nomadstays.jpeg",
				title: "Marketing Intern",
				location: "",
				duration: "Full Time",
				company: "Nomad Stays",
				isRemote: false,
				applyLink: "https://www.nomadstays.com/jobs",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://proptechbuzz-dev.s3.amazonaws.com/others-job-board/housewise.png",
				title: "Operations Associate",
				location:
					"Thane / Palghar / Navi Mumbai / Mumbai / Pune / Goa / Hyderabad / Bangalore / Chennai / Delhi-NCR",
				duration: "Full Time",
				company: "Housewise",
				isRemote: false,
				applyLink: "https://housewise.in/careers-with-housewise",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Nirupa Shankar",
					link: "https://www.proptechbuzz.com/user/nirupa-875545",
				},
				{
					name: "Ashwinder R. Singh",
					link: "https://www.proptechbuzz.com/user/ashwinder-641349",
				},
				{
					name: "Sanjay Mandava",
					link: "https://www.proptechbuzz.com/user/sanjay_mandava-221177",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "Virtual Homes",
					link: "https://www.proptechbuzz.com/products/virtual-homes",
				},
				{
					name: "YourOwnROOM",
					link: "https://www.proptechbuzz.com/products/yourownroom",
				},
				{
					name: "PropCheckup Home Inspection",
					link: "https://www.proptechbuzz.com/products/propcheckup-home-inspection",
				},
			],
		},
		{
			title: "discussions",
			values: [
				{
					name: "Customer Education : How are you going about it?",
					link: "https://www.proptechbuzz.com/communities/global-proptech-forum/vip-intro/customer-education-how-are-you-going-about-it-68001148",
				},
				{
					name: "Dheeraj S, Fund Manager of Earth Fund talks about Proptech Ecosystem in India",
					link: "https://www.proptechbuzz.com/communities/proptech-india/vip-india-channel/dheeraj-s-fund-manager-of-earth-fund-talks-about-proptech-ecosystem-in-india-16709305",
				},
				{
					name: "What'd you like to see in Proptech India community?",
					link: "https://www.proptechbuzz.com/communities/proptech-india/general-395250/what-d-you-like-to-see-in-proptech-india-community-how-do-yo-398945",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "Proptech India",
					link: "https://www.proptechbuzz.com/communities/proptech-india",
				},
				{
					name: "Smarter Real Estate",
					link: "https://www.proptechbuzz.com/communities/smarter-real-estate",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "Vibrant BuildCon",
					link: "https://vibrantbuildcon.com/",
				},
				{
					name: "Mapin India - Shopping Centre Retail Summit",
					link: "https://www.mapic-india.in/en-gb.html",
				},
				{
					name: " AMA with Amit, Co-founder & CEO of NoBroker",
					link: "https://www.proptechbuzz.com/events/ama-amit-kumar-nobroker",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "India Smart Sustainable Homes",
					link: "https://www.proptechbuzz.com/news/india-smart-sustainable-homes",
				},
				{
					name: "Role of NRI in Bengalurus Property Market",
					link: "https://www.proptechbuzz.com/news/role-of-nri-in-bengalurus-property-market",
				},
				{
					name: "AI Replace Real Estate Brokers in India",
					link: "https://www.proptechbuzz.com/news/ai-replace-real-estate-brokers-in-india",
				},
			],
		},
	],
};

export const COUNTRY_UAE_DATA: CountryContextType = {
	name: "United Arab Emirates",
	code: "UAE",
	country: ["United Arab Emirates"],

	hero: {
		backgroundImage: "/images/communities/country-portal/uae-header-banner.png",
		flagImage: "/images/communities/country-portal/uae-flag.png",
		flagAlt: "UAE",
		title: "Discover UAE Proptech",
		screenReaderTitle: "Discover UAE Proptech Products",
		description:
			"One stop shop to explore proptech technologies in UAE. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech UAE",
	},

	ambassadors: [
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/e6337a0d-8084-4bd3-845d-78ab05d2d3f2.gif`,
			name: "Ravi Kumar Sapata",
			company: "Founder/CEO | PropTechBuzz",
			profileLink: "https://www.proptechbuzz.com/user/@ravi",
		},
	],

	vips: {
		title: "Our Premium VIPs from UAE",
	},

	products: {
		code: "UAE",
		perPage: 80,
	},

	stats: {
		products: "200+",
		members: "2500+",
		views: "124.4k",
		communities: "1200+",
		courses: "800+",
	},

	portals: {
		current: {
			labelText: "Hot",
			title: "PropTech UAE",
			labelTextClassName: "bg-red-500",
			href: "/communities/proptech-uae/home",
			logo: "/images/communities/country-portal/uae-flag.png",
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech India",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-india/home",
				logo: "/images/communities/country-portal/prop-tech-india.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech Austria",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-austria.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt:
			"Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
		videoUrl: "https://www.youtube-nocookie.com/embed/o9LYjfpyd7E?autoplay=1&start=0&mute=1",
		videoTitle:
			"Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
		text: "Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
	},

	jobBoard: {
		title: "UAE PropTech Jobs",
		jobs: [
			{
				logo: "https://proptechbuzz-dev.s3.amazonaws.com/others-job-board/accacia.png",
				title: "Head of Growth",
				location: "Bangalore - India, Singapore",
				duration: "Full Time",
				company: "Accacia",
				isRemote: false,
				applyLink:
					"https://dynamic-polka-cd9.notion.site/Careers-07495d488c8e4af5a07d40709f6dadbf?p=e8e0200f29c242d4be70d1f772e99290&pm=c",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://proptechbuzz-dev.s3.amazonaws.com/others-job-board/nomadstays.jpeg",
				title: "Marketing Intern",
				location: "",
				duration: "Full Time",
				company: "Nomad Stays",
				isRemote: false,
				applyLink: "https://www.nomadstays.com/jobs",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://proptechbuzz-dev.s3.amazonaws.com/others-job-board/housewise.png",
				title: "Operations Associate",
				location:
					"Thane / Palghar / Navi Mumbai / Mumbai / Pune / Goa / Hyderabad / Bangalore / Chennai / Delhi-NCR",
				duration: "Full Time",
				company: "Housewise",
				isRemote: false,
				applyLink: "https://housewise.in/careers-with-housewise",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Nirupa Shankar",
					link: "https://www.proptechbuzz.com/user/nirupa-875545",
				},
				{
					name: "Ashwinder R. Singh",
					link: "https://www.proptechbuzz.com/user/ashwinder-641349",
				},
				{
					name: "Sanjay Mandava",
					link: "https://www.proptechbuzz.com/user/sanjay_mandava-221177",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "Virtual Homes",
					link: "https://www.proptechbuzz.com/products/virtual-homes",
				},
				{
					name: "YourOwnROOM",
					link: "https://www.proptechbuzz.com/products/yourownroom",
				},
				{
					name: "PropCheckup Home Inspection",
					link: "https://www.proptechbuzz.com/products/propcheckup-home-inspection",
				},
			],
		},
		{
			title: "discussions",
			values: [
				{
					name: "Customer Education : How are you going about it?",
					link: "https://www.proptechbuzz.com/communities/global-proptech-forum/vip-intro/customer-education-how-are-you-going-about-it-68001148",
				},
				{
					name: "Dheeraj S, Fund Manager of Earth Fund talks about Proptech Ecosystem in India",
					link: "https://www.proptechbuzz.com/communities/proptech-india/vip-india-channel/dheeraj-s-fund-manager-of-earth-fund-talks-about-proptech-ecosystem-in-india-16709305",
				},
				{
					name: "What'd you like to see in Proptech India community?",
					link: "https://www.proptechbuzz.com/communities/proptech-india/general-395250/what-d-you-like-to-see-in-proptech-india-community-how-do-yo-398945",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "Proptech India",
					link: "https://www.proptechbuzz.com/communities/proptech-india",
				},
				{
					name: "Smarter Real Estate",
					link: "https://www.proptechbuzz.com/communities/smarter-real-estate",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "Vibrant BuildCon",
					link: "https://vibrantbuildcon.com/",
				},
				{
					name: "Mapin India - Shopping Centre Retail Summit",
					link: "https://www.mapic-india.in/en-gb.html",
				},
				{
					name: " AMA with Amit, Co-founder & CEO of NoBroker",
					link: "https://www.proptechbuzz.com/events/ama-amit-kumar-nobroker",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "India Smart Sustainable Homes",
					link: "https://www.proptechbuzz.com/news/india-smart-sustainable-homes",
				},
				{
					name: "Role of NRI in Bengalurus Property Market",
					link: "https://www.proptechbuzz.com/news/role-of-nri-in-bengalurus-property-market",
				},
				{
					name: "AI Replace Real Estate Brokers in India",
					link: "https://www.proptechbuzz.com/news/ai-replace-real-estate-brokers-in-india",
				},
			],
		},
	],
};

export const COUNTRY_AUSTRIA_DATA: CountryContextType = {
	name: "Austria",
	code: "AT",
	country: ["Austria"],

	meta: {
		name: {
			title: "Austria",
			value: "austria",
			adjective: "Austrian",
		},
	},

	hero: {
		backgroundImage: "/images/communities/country-portal/austria-header-banner.png",
		flagImage: "/images/communities/country-portal/austria-flag.png",
		flagAlt: "Austria",
		title: "Discover Austria Proptech",
		screenReaderTitle: "Discover Austria Proptech Products",
		description:
			"One stop shop to explore proptech technologies in Austria. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech Austria",
	},

	ambassadors: [
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/369dfbe9-d9a2-44fe-b502-942d41d70de7.jpg`,
			name: "Jörg Buß",
			company: "Co-founder and Board Member | DREA Digital Real Estate Association",
			profileLink: "https://www.proptechbuzz.com/user/jrg-bu-738372",
		},
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/23e134bd-75e2-48c9-b015-4ccb2a3173ec.jpg`,
			name: "Sandra Aichholzer-Imre",
			company: "Managing Director | DREA Digital Real Estate Association",
			profileLink: "https://www.proptechbuzz.com/user/sandra-aichholzerimre-215247",
		},
	],

	vips: {
		title: "Our Premium VIPs from Austria",
	},

	products: {
		perPage: 80,
	},

	stats: {
		products: "200+",
		members: "2500+",
		views: "124.4k",
		communities: "1200+",
		courses: "800+",
	},

	portals: {
		current: {
			labelText: "Hot",
			title: "PropTech Austria",
			labelTextClassName: "bg-red-500",
			href: "/communities/proptech-austria/home",
			logo: "/images/communities/country-portal/austria-flag.png",
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech India",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-india/home",
				logo: "/images/communities/country-portal/prop-tech-india.png",
			},
			{
				labelText: "Hot",
				title: "PropTech UAE",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-uae.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt: "Austria Proptech Portal Launch 🚀 🇦🇹",
		videoUrl: "https://www.youtube-nocookie.com/embed/aaK8HUmjUBk?autoplay=1&start=0&mute=1",
		videoTitle: "Austria Proptech Portal Launch 🚀 🇦🇹",
		text: "Austria Proptech Portal Launch 🚀 🇦🇹",
	},

	jobBoard: {
		title: "Austria PropTech Jobs",
		jobs: [
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/e-frontiers.jpeg`,
				title: "Senior Project Manager(Construction)",
				location: "Linz, Upper Austria, Austria",
				duration: "Full Time",
				company: "E-Frontiers",
				isRemote: false,
				applyLink:
					"https://www.linkedin.com/jobs/view/senior-project-manager-main-contractor-construction-at-e-frontiers-4222232845/?originalSubdomain=at",
				lastUpdated: "1 day ago",
			},
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/abb.jpeg`,
				title: "Real Estate Specialist Administration",
				location: "Eggelsberg, Upper Austria, Austria",
				duration: "Full Time",
				company: "ABB",
				isRemote: false,
				applyLink:
					"https://www.linkedin.com/jobs/view/real-estate-specialist-administration-b-r-automation-at-abb-ventures-4208170110/?originalSubdomain=at",
				lastUpdated: "1 day ago",
			},
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/bawag-group.jpeg`,
				title: "Real Estate Manager",
				location: "Vienna, Vienna, Austria",
				duration: "Full Time",
				company: "BAWAG Group",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4221215308",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Julia Arlt",
					link: "https://www.proptechbuzz.com/user/julia_arlt-515745",
				},
				{
					name: "Christina",
					link: "https://www.proptechbuzz.com/user/christina-833301",
				},
				{
					name: "Ferdinand Dietrich",
					link: "https://www.proptechbuzz.com/user/ferdinand-dietrich-817509",
				},
				{
					name: "Manfred Heid",
					link: "https://www.proptechbuzz.com/user/manfred-heid-146574",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "Aareon",
					link: "https://www.proptechbuzz.com/products/aareon",
				},
				{
					name: "FlexOfficeAgency",
					link: "https://www.proptechbuzz.com/products/flexofficeagency",
				},
				{
					name: "smino",
					link: "https://www.proptechbuzz.com/products/smino",
				},
			],
		},
		{
			title: "discussion",
			values: [
				{
					name: "DREA AWARD 2025 - Applications Open!",
					link: "https://www.proptechbuzz.com/communities/proptech-austria/proptech-austria-general/-495668",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "Digital Real Estate Association (DREA) Community",
					link: "https://www.proptechbuzz.com/communities/drea",
				},
				{
					name: "Proptech Austria",
					link: "https://www.proptechbuzz.com/communities/proptech-austria",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "CEE Property Forum & Awards Gala 2025",
					link: "https://www.property-forum.eu/forums/conference-property/cee-property-forum-awards-gala-2025-vienna-austria/2020",
				},
				{
					name: "Real Estate Circle 2025",
					link: "https://www.businesscircle.at/events/real-estate-circle-2025",
				},
				{
					name: "3rd International Real Estate Investment Congress",
					link: "https://kongress.immobilien-investment.at/",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "Will Austria's property market become more buyer-friendly now?",
					link: "https://www.proptechbuzz.com/news/austrias-property-market-become-more-buyer-friendly",
				},
				{
					name: "Rents in Austria hit record highs as three states cross €20 per square metre",
					link: "https://www.proptechbuzz.com/news/rents-in-austria-hit-record-highs-as-three-states-cross-e20-per-square-metre",
				},
				{
					name: "The changes property buyers and renters can expect in Austria in 2025",
					link: "https://www.proptechbuzz.com/news/austria-housing-market-2025-changes",
				},
			],
		},
	],

	popularTabs: {
		communities: [
			{
				labelText: "New",
				title: "Digital Real Estate Association (DREA) Community",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/drea`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/drea/drea-logo.webp`,
			},
			{
				labelText: "New",
				title: "Proptech Austria",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/proptech-austria`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-austria/proptech-austria-logo.png`,
			},
		],
		people: [
			{
				name: "Martin Renezeder",
				username: "martin-renezeder-363504",
				jobTitle: "Stabstelle für Digitalisierung und Innovation",
				company: "BUWOG Group GmbH",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/0e321d16-cf38-4f65-afc9-80868fb65931.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Sandra Aichholzer-Imre",
				username: "sandra-aichholzerimre-215247",
				jobTitle: "Managing Director",
				company: "DREA Digital Real Estate Association",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/23e134bd-75e2-48c9-b015-4ccb2a3173ec.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Julia Arlt",
				username: "julia_arlt-515745",
				jobTitle: "Co-Founder & Venture Partner & Investor & Advisor",
				company: "FuturePlace / DREA / PTE PropTech Events/ Realyze Ventures / Optiml / etc.",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/13968c86-ff65-4723-a22f-3343f4a57d14.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Jörg Buß",
				username: "jrg-bu-738372",
				jobTitle: "Co-founder and Board Member",
				company: "DREA Digital Real Estate Association",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/369dfbe9-d9a2-44fe-b502-942d41d70de7.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
		],
		products: [
			{
				name: "Aareon",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/3740f679-332d-4189-a10c-d3efc7ceac92/logo/f08ff405-4c97-4e79-91ae-686eb7af6db3.png`,
				slug: "aareon",
			},
			{
				name: "FlexOfficeAgency",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/a469cc06-28f4-438e-b509-fb31ca714ae3/logo/3167c10b-b200-41bf-8dc6-5e6e9c9a702d.jpeg`,
				slug: "flexofficeagency",
			},
			{
				name: "smino",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/913a0b22-d54c-426a-91c0-71a3ceb02fdb/logo/49580dd2-a3f0-4a0e-9a97-26e0c0342654.jpeg`,
				slug: "smino",
			},
		],
	},
};

export const COUNTRY_GERMANY_DATA: CountryContextType = {
	name: "Germany",
	code: "DE",
	country: ["Germany"],

	meta: {
		name: {
			title: "Germany",
			value: "germany",
			adjective: "German",
		},
	},

	hero: {
		backgroundImage: "/images/communities/country-portal/austria-header-banner.png",
		flagImage: "/images/communities/country-portal/germany-flag.png",
		flagAlt: "Germany",
		title: "Discover Germany Proptech",
		screenReaderTitle: "Discover Germany Proptech Products",
		description:
			"One stop shop to explore proptech technologies in Germany. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech Germany",
	},

	ambassadors: [
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/f157c36e-ed98-442b-aff8-99a9bd391881.png`,
			name: "Ferdinand Dietrich",
			company: "Co-Founder | Storebox",
			profileLink: "https://www.proptechbuzz.com/user/ferdinand-dietrich-817509",
		},
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/23e134bd-75e2-48c9-b015-4ccb2a3173ec.jpg`,
			name: "Sandra Aichholzer-Imre",
			company: "Managing Director | DREA Digital Real Estate Association",
			profileLink: "https://www.proptechbuzz.com/user/sandra-aichholzerimre-215247",
		},
	],

	vips: {
		title: "Our Premium VIPs from Germany",
	},

	products: {
		perPage: 80,
	},

	stats: {
		products: "500+",
		members: "2500+",
		views: "124.4k",
		communities: "24",
		courses: "55+",
	},

	portals: {
		current: {
			labelText: "Hot",
			title: "PropTech Germany",
			labelTextClassName: "bg-red-500",
			href: "/communities/proptech-germany/home",
			logo: "/images/communities/country-portal/germany-flag.png",
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech Austria",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-austria/home",
				logo: "/images/communities/country-portal/austria-flag.png",
			},
			{
				labelText: "Hot",
				title: "PropTech India",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-india/home",
				logo: "/images/communities/country-portal/prop-tech-india.png",
			},
			{
				labelText: "Hot",
				title: "PropTech UAE",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-uae.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt: "Germany Country Portal Launch 🚀🇩🇪",
		videoUrl: "https://www.youtube-nocookie.com/embed/loB-QgLk0uE?autoplay=1&start=0&mute=1",
		videoTitle: "Germany Country Portal Launch 🚀🇩🇪",
		text: "Germany Country Portal Launch 🚀🇩🇪",
	},

	jobBoard: {
		title: "Germany PropTech Jobs",
		jobs: [
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/exyte.jpg`,
				title: "Construction Director",
				location: "Nuremberg, Bavaria, Germany",
				duration: "Full Time",
				company: "Exyte",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4123557400",
				lastUpdated: "1 day ago",
			},
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/morningstarDBRS.jpg`,
				title: "Vice President, Credit Ratings, Real Estate",
				location: "Frankfurt am Main, Hesse, Germany",
				duration: "Full Time",
				company: "Morningstar DBRS",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4214837243",
				lastUpdated: "1 day ago",
			},
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/macdonaldAndCompany.jpg`,
				title: "Investment Analyst - Real Estate",
				location: "Berlin, Berlin, Germany",
				duration: "Full Time",
				company: "Macdonald & Company",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4222471598",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Christina",
					link: "https://www.proptechbuzz.com/user/christina-833301",
				},
				{
					name: "Ferdinand Dietrich",
					link: "https://www.proptechbuzz.com/user/ferdinand-dietrich-817509",
				},
				{
					name: "Nico Dehnert",
					link: "https://www.proptechbuzz.com/user/nico-dehnert-988842",
				},
				{
					name: "Angie Mahtaney",
					link: "https://www.proptechbuzz.com/user/angie-mahtaney-745594",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "Colonies",
					link: "https://www.proptechbuzz.com/products/colonies",
				},
				{
					name: "DocBee",
					link: "https://www.proptechbuzz.com/products/docbee",
				},
				{
					name: "Wohnglück.de",
					link: "https://www.proptechbuzz.com/products/wohngl-ck-de",
				},
			],
		},
		{
			title: "discussion",
			values: [
				{
					name: "DREA AWARD 2025 - Applications Open!",
					link: "https://www.proptechbuzz.com/communities/proptech-germany/general-457615/-372815",
				},
				{
					name: "Welcome to Proptech Germany community!",
					link: "https://www.proptechbuzz.com/communities/proptech-germany/general-457615/welcome-to-proptech-germany-community-631802",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "Digital Real Estate Association (DREA) Community",
					link: "https://www.proptechbuzz.com/communities/drea",
				},
				{
					name: "Proptech Germany",
					link: "https://www.proptechbuzz.com/communities/proptech-germany",
				},
				{
					name: "ATechX",
					link: "https://www.proptechbuzz.com/communities/atechx",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "EXPO REAL 2025",
					link: "https://exporeal.net/en/",
				},
				{
					name: "3rd International Summit on Civil, Structural and Environmental Engineering (ISCSEE 2025)",
					link: "https://civilsummit.com/",
				},
				{
					name: "Real Estate Arena 2025",
					link: "https://www.real-estate-arena.com/de/",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "Germany Eases Mortgage Capital Rules as Housing Market Stabilizes",
					link: "https://www.proptechbuzz.com/news/germany-mortgage-capital-buffer-cut",
				},
				{
					name: "Germany Faces Bridge Renovation Delays, Auditors Urge Higher Funding",
					link: "https://www.proptechbuzz.com/news/germany-bridge-renovation-delays",
				},
				{
					name: "Germany's Residential Real Estate Investment Market Shows Signs of Steady Recovery",
					link: "https://www.proptechbuzz.com/news/germany-residential-real-estate-investment-q3",
				},
			],
		},
	],

	popularTabs: {
		communities: [
			{
				labelText: "New",
				title: "Digital Real Estate Association (DREA) Community",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/drea`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/drea/drea-logo.webp`,
			},
			{
				labelText: "New",
				title: "Proptech Germany",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/proptech-germany`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-germany/proptech-germany-logo.png`,
			},
			{
				labelText: "New",
				title: "ATechX",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/atechx`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/communities/atechx/atech-x-logo.jpeg`,
			},
		],
		people: [
			{
				name: "Christina",
				username: "christina-833301",
				jobTitle: "CEO",
				company: "einwert",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/0927ccf2-2ed3-4f23-a176-73b744ca671c.jpeg`,
				location: "Germany",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Ferdinand Dietrich",
				username: "ferdinand-dietrich-817509",
				jobTitle: "Co-Founder",
				company: "Storebox",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/f157c36e-ed98-442b-aff8-99a9bd391881.png`,
				location: "Germany",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Nico Dehnert",
				username: "nico-dehnert-988842",
				jobTitle: "Co-Founder & CCO",
				company: "Optiml AG",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/4b79e61f-97b7-4317-94d7-65b6e66b6e02/founders/9f484a60-81b1-4edb-94fe-e902d22a032d.png`,
				location: "Germany",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Angie Mahtaney",
				username: "angie-mahtaney-745594",
				jobTitle: "Proptech Investor",
				company: "Aroundtown SA",
				profileImage:
					"https://media.licdn.com/dms/image/D5603AQHGFWVaGqtzsA/profile-displayphoto-shrink_100_100/0/1674622278179?e=2147483647&v=beta&t=dSOjwml_XWfMl2vki-yp48KnCrC36GkIEC4Y4GsXl_M",
				location: "Germany",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Manfred Heid",
				username: "manfred-heid-146574",
				jobTitle: "General Partner",
				company: "Realyze Ventures",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/528b8888-5d12-45d4-ae5e-9c90675cdc46.png`,
				location: "Germany",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Ivan Nokhrin",
				username: "ivan-169577",
				jobTitle: "CEO & Co-founder",
				company: "BuiltAPI GmbH",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/2b0383a7-02e3-44f3-9ec3-4385b30a0635.jpg`,
				location: "Germany",
				isFounder: true,
				isVerified: true,
			},
		],
		products: [
			{
				name: "Colonies",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/811790b3-d7b3-4d42-b47c-47391636e873/logo/b17b88e4-8666-4dc0-8382-6d6e1e6a61c5.jpeg`,
				slug: "colonies",
			},
			{
				name: "DocBee",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/433a56c3-3a05-49f5-b93e-a82626ef1ca4/logo/01c396f7-5fc5-4754-ba8e-971f50209e8d.jpeg`,
				slug: "docbee",
			},
			{
				name: "Wohnglück.de",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/3f747b29-c494-474d-ad1a-f1bfc64a21af/logo/0cac2335-2456-426b-a61c-6f69fe78f456.jpeg`,
				slug: "wohngl-ck-de",
			},
			{
				name: "Catenda",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/27bf5239-fa61-45dd-9ca8-c14ec8b7a1e9/logo/4c6360ac-dc28-4391-b504-aa9b0fa6689e.jpeg`,
				slug: "catenda",
			},
			{
				name: "Doozer",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/cb5bf3cf-d6c5-4461-8191-434e6e4d5bfc/logo/4fccb991-4f60-4bf9-9866-65f663fd0c88.jpeg`,
				slug: "doozer",
			},
		],
	},
};

//TODO: Update data with USA country portal, using it only for zeroState search
export const COUNTRY_UNITED_STATES_DATA: CountryContextType = {
	name: "UNITED STATES",
	code: "USA",
	country: ["Austria"],

	meta: {
		name: {
			title: "United States",
			value: "united-states",
			adjective: "American",
		},
	},

	hero: {
		backgroundImage: "/images/communities/country-portal/austria-header-banner.png",
		flagImage: "/images/communities/country-portal/united-states-flag.png",
		flagAlt: "United States",
		title: "Discover United States Proptech",
		screenReaderTitle: "Discover United States Proptech Products",
		description:
			"One stop shop to explore proptech technologies in United States. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech United States",
	},

	ambassadors: [],

	vips: {
		title: "Our Premium VIPs from Austria",
	},

	products: {
		perPage: 80,
	},

	stats: {
		products: "200+",
		members: "2500+",
		views: "124.4k",
		communities: "1200+",
		courses: "800+",
	},

	portals: {
		current: {
			labelText: "Hot",
			title: "PropTech Austria",
			labelTextClassName: "bg-red-500",
			href: "/communities/proptech-austria/home",
			logo: "/images/communities/country-portal/austria-flag.png",
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech India",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-india/home",
				logo: "/images/communities/country-portal/prop-tech-india.png",
			},
			{
				labelText: "Hot",
				title: "PropTech UAE",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-uae.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt:
			"Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
		videoUrl: "https://www.youtube-nocookie.com/embed/o9LYjfpyd7E?autoplay=1&start=0&mute=1",
		videoTitle:
			"Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
		text: "Proptech Showcase Disrupt - IPS Proptech Hub, Dubai - UAE's Largest Proptech Conference",
	},

	jobBoard: {
		title: "Austria PropTech Jobs",
		jobs: [
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/e-frontiers.jpeg`,
				title: "Senior Project Manager(Construction)",
				location: "Linz, Upper Austria, Austria",
				duration: "Full Time",
				company: "E-Frontiers",
				isRemote: false,
				applyLink:
					"https://www.linkedin.com/jobs/view/senior-project-manager-main-contractor-construction-at-e-frontiers-4222232845/?originalSubdomain=at",
				lastUpdated: "1 day ago",
			},
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/abb.jpeg`,
				title: "Real Estate Specialist Administration",
				location: "Eggelsberg, Upper Austria, Austria",
				duration: "Full Time",
				company: "ABB",
				isRemote: false,
				applyLink:
					"https://www.linkedin.com/jobs/view/real-estate-specialist-administration-b-r-automation-at-abb-ventures-4208170110/?originalSubdomain=at",
				lastUpdated: "1 day ago",
			},
			{
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/others-job-board/bawag-group.jpeg`,
				title: "Real Estate Manager",
				location: "Vienna, Vienna, Austria",
				duration: "Full Time",
				company: "BAWAG Group",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4221215308",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Kevin Shtofman",
					link: "/user/kevin_shtofman-865967",
				},
				{
					name: "Brandon Peterson",
					link: "/user/brandon-870218",
				},
				{
					name: "Carolyn",
					link: "/user/carolyn-947210",
				},
				{
					name: "Matt Knight",
					link: "/user/matt-knight-516877",
				},
				{
					name: "Amy Polvado",
					link: "/user/amy-polvado-035590",
				},
				{
					name: "Amy Gorce",
					link: "/user/amy-gorce-532059",
				},
				{
					name: "Kevin Greene",
					link: "/user/kevin_greene-157656",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "Gena",
					link: "/products/gena",
				},
				{
					name: "CanaryAI Beta",
					link: "/products/canary-ai-beta",
				},
				{
					name: "Lighthouse",
					link: "/products/lighthouse",
				},
				{
					name: "RentalGuard",
					link: "/products/rentaguard",
				},
				{
					name: "FORTRESS",
					link: "/products/fortress",
				},
			],
		},
		{
			title: "discussion",
			values: [
				{
					name: "DREA AWARD 2025 - Applications Open!",
					link: "https://www.proptechbuzz.com/communities/proptech-austria/proptech-austria-general/-495668",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "Digital Real Estate Association (DREA) Community",
					link: "https://www.proptechbuzz.com/communities/drea",
				},
				{
					name: "Proptech Austria",
					link: "https://www.proptechbuzz.com/communities/proptech-austria",
				},
				{
					name: "Proptech Germany",
					link: "https://www.proptechbuzz.com/communities/proptech-germany",
				},
				{
					name: "Proptech India",
					link: "https://www.proptechbuzz.com/communities/proptech-india",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "CEE Property Forum & Awards Gala 2025",
					link: "https://www.property-forum.eu/forums/conference-property/cee-property-forum-awards-gala-2025-vienna-austria/2020",
				},
				{
					name: "Real Estate Circle 2025",
					link: "https://www.businesscircle.at/events/real-estate-circle-2025",
				},
				{
					name: "3rd International Real Estate Investment Congress",
					link: "https://kongress.immobilien-investment.at/",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "Will Austria's property market become more buyer-friendly now?",
					link: "https://www.proptechbuzz.com/news/austrias-property-market-become-more-buyer-friendly",
				},
				{
					name: "Rents in Austria hit record highs as three states cross €20 per square metre",
					link: "https://www.proptechbuzz.com/news/rents-in-austria-hit-record-highs-as-three-states-cross-e20-per-square-metre",
				},
				{
					name: "The changes property buyers and renters can expect in Austria in 2025",
					link: "https://www.proptechbuzz.com/news/austria-housing-market-2025-changes",
				},
			],
		},
	],

	popularTabs: {
		communities: [
			{
				labelText: "New",
				title: "Digital Real Estate Association (DREA) Community",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/drea`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/drea/drea-logo.webp`,
			},
			{
				labelText: "New",
				title: "Proptech Austria",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/proptech-austria`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-austria/proptech-austria-logo.png`,
			},
		],
		people: [
			{
				name: "Martin Renezeder",
				username: "martin-renezeder-363504",
				jobTitle: "Stabstelle für Digitalisierung und Innovation",
				company: "BUWOG Group GmbH",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/0e321d16-cf38-4f65-afc9-80868fb65931.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Sandra Aichholzer-Imre",
				username: "sandra-aichholzerimre-215247",
				jobTitle: "Managing Director",
				company: "DREA Digital Real Estate Association",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/23e134bd-75e2-48c9-b015-4ccb2a3173ec.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Julia Arlt",
				username: "julia_arlt-515745",
				jobTitle: "Co-Founder & Venture Partner & Investor & Advisor",
				company: "FuturePlace / DREA / PTE PropTech Events/ Realyze Ventures / Optiml / etc.",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/13968c86-ff65-4723-a22f-3343f4a57d14.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Jörg Buß",
				username: "jrg-bu-738372",
				jobTitle: "Co-founder and Board Member",
				company: "DREA Digital Real Estate Association",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/369dfbe9-d9a2-44fe-b502-942d41d70de7.jpg`,
				location: "Austria",
				isFounder: true,
				isVerified: true,
			},
		],
		products: [
			{
				name: "Aareon",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/3740f679-332d-4189-a10c-d3efc7ceac92/logo/f08ff405-4c97-4e79-91ae-686eb7af6db3.png`,
				slug: "aareon",
			},
			{
				name: "FlexOfficeAgency",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/a469cc06-28f4-438e-b509-fb31ca714ae3/logo/3167c10b-b200-41bf-8dc6-5e6e9c9a702d.jpeg`,
				slug: "flexofficeagency",
			},
			{
				name: "smino",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/913a0b22-d54c-426a-91c0-71a3ceb02fdb/logo/49580dd2-a3f0-4a0e-9a97-26e0c0342654.jpeg`,
				slug: "smino",
			},
		],
	},
};

export const COUNTRY_BANGLADESH_DATA: CountryContextType = {
	name: "Bangladesh",
	code: "BD",
	country: ["Bangladesh"],

	meta: {
		name: {
			title: "Bangladesh",
			value: "Bangladesh",
			adjective: "Bangladeshi",
		},
	},

	hero: {
		backgroundImage: "/images/communities/country-portal/bangladesh-header-banner.png",
		flagImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-bangladesh/proptech-bangladesh-logo.png`,
		flagAlt: "Bangladesh",
		title: "Discover Bangladesh Proptech",
		screenReaderTitle: "Discover Bangladesh Proptech Products",
		description:
			"One stop shop to explore proptech technologies in Bangladesh. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech Bangladesh",
	},

	ambassadors: [
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/243cc8da-687a-4656-a23b-e45c5d2b0674.jpeg`,
			name: "Arif Hasan",
			company: "UXPro Labs Limited",
			profileLink: "https://www.proptechbuzz.com/user/arif_hasan-270873",
		},
		{
			image:
				"https://lh3.googleusercontent.com/a/ACg8ocKCMuZSNn6Z8zAhfrmWJ15GM26p-LwZuxk9PRDY4QpcPNcUdv2V=s96-c",
			name: "Sadman Sakib Ayon",
			company: "Property Khobor",
			profileLink: "https://www.proptechbuzz.com/user/sadman-sakib-ayon-571936",
		},
	],

	vips: {
		title: "Our Premium VIPs from Bangladesh",
	},

	products: {
		perPage: 80,
	},

	stats: {
		products: "200+",
		members: "2500+",
		views: "124.4k",
		communities: "1200+",
		courses: "800+",
	},

	portals: {
		current: {
			labelText: "Hot",
			title: "PropTech Bangladesh",
			labelTextClassName: "bg-red-500",
			href: "/communities/proptech-bangladesh/home",
			logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-bangladesh/proptech-bangladesh-logo.png`,
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech India",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-india/home",
				logo: "/images/communities/country-portal/prop-tech-india.png",
			},
			{
				labelText: "Hot",
				title: "PropTech UAE",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-uae/home",
				logo: "/images/communities/country-portal/uae-flag.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech Austria",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-austria.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt: "Bangladesh PropTech Portal Launch 🇧🇩",
		videoUrl: "https://www.youtube-nocookie.com/embed/Qddw2NmUGHM?autoplay=1&start=0&mute=1",
		videoTitle: "Bangladesh PropTech Portal Launch",
		text: "Bangladesh PropTech Portal Launch",
	},

	jobBoard: {
		title: "Bangladesh PropTech Jobs",
		jobs: [
			{
				logo: "https://media.licdn.com/dms/image/v2/D4D0BAQHphxemEZ3JoQ/company-logo_100_100/B4DZfvVWitHAAQ-/0/1752067041789/db_engineering_consulting_logo?e=1755734400&v=beta&t=xR0XvhSeMvZafYAGY2oros0k5K06zFBTt_o8Kz4qe-c",
				title: "Construction Schedule Specialist",
				location: "Dhaka, Bangladesh",
				duration: "Full Time",
				company: "DB Engineering & Consulting",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4254881131",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://media.licdn.com/dms/image/v2/C510BAQH8ptLnwxujHA/company-logo_100_100/company-logo_100_100/0/1631318384635?e=1755734400&v=beta&t=0kx09OmNDJgfvJhVaHhBoac1HGBhh6eCl-MI-cvEYXM",
				title: "Project Engineer",
				location: "Bangladesh",
				duration: "Full Time",
				company: "Building technology & ideas ltd. - bti",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4187284489",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://media.licdn.com/dms/image/v2/D560BAQHIsBqGHskdjg/company-logo_100_100/company-logo_100_100/0/1730114573321/mybdjobs_logo?e=1755734400&v=beta&t=hfNufmTLDsAO--62gOzz_QOI2HzV_p4Rzwmhu0SUNUA",
				title: "Civil Engineer",
				location: "Bangladesh",
				duration: "Full Time",
				company: "Bdjobs.com",
				isRemote: false,
				applyLink: "https://www.linkedin.com/jobs/view/4264430412",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Arif Hasan",
					link: "https://www.proptechbuzz.com/user/arif_hasan-270873",
				},
				{
					name: "MD Rabby Hossain",
					link: "https://www.proptechbuzz.com/user/md-rabby-hossain-808830",
				},
				{
					name: "Md Zuhabul Islam",
					link: "https://www.proptechbuzz.com/user/md-zuhabul-islam-919065",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "Abasan BD",
					link: "http://proptechbuzz.com/products/abasan-bd",
				},
				{
					name: "GEN Z Property Talks",
					link: "https://www.proptechbuzz.com/products/gen-z-property-talks",
				},
				{
					name: "Property Studio",
					link: "https://www.proptechbuzz.com/products/property-studio",
				},
			],
		},
		{
			title: "discussion",
			values: [
				{
					name: "ProptechBuzz Bangladesh Country Portal Launch",
					link: "https://www.proptechbuzz.com/communities/proptech-bangladesh/proptech-bangladesh-general/proptechbuzz-bangladesh-country-portal-launch-177301",
				},
				{
					name: "Turning Around Bangladesh's Housing Sector Crisis!",
					link: "https://www.proptechbuzz.com/communities/proptech-bangladesh/proptech-bangladesh-general/turning-around-bangladesh-s-housing-sector-crisis-958967",
				},
				{
					name: "Welcome Arif Hasan as Official Ambassador to Proptech Bangladesh Community!",
					link: "https://www.proptechbuzz.com/communities/proptech-bangladesh/proptech-bangladesh-general/welcome-arif-hasan-as-official-ambassador-to-proptech-bangla-854661",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "Proptech Bangladesh",
					link: "https://www.proptechbuzz.com/communities/proptech-bangladesh",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "24th Real Estate Expo 2025",
					link: "https://cems-realestateexpo.com/",
				},
				{
					name: "30th Build Bangladesh International Expo 2025",
					link: "https://www.build-bangladesh.com/",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "Top Halal Investment Options In Bangladesh For 2025",
					link: "https://www.proptechbuzz.com/news/halal-investment-options-bangladesh",
				},
				{
					name: "Bproperty and Rupayan Group launch joint venture to transform Bangladesh's real estate sector",
					link: "https://www.proptechbuzz.com/news/bproperty-rupayan-strategic-partnership-bangladesh-real-estate",
				},
				{
					name: "Bangladesh Construction Industry Databook 2025",
					link: "https://www.proptechbuzz.com/news/bangladesh-construction-market-forecast",
				},
			],
		},
	],

	popularTabs: {
		communities: [
			{
				labelText: "New",
				title: "Proptech Bangladesh",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/proptech-bangladesh`,
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-bangladesh/proptech-bangladesh-logo.png`,
			},
		],
		people: [
			{
				name: "Arif Hasan",
				username: "arif_hasan-270873",
				jobTitle: "Co/Founder/CXO",
				company: "UXPro Labs Limited ",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/6d7e8e0f-8e4e-4291-879d-d283ccc72591/founders/be0ceca3-9c21-4c7f-9fe2-d4faf5e67bf5.png`,
				location: "Bangladesh",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "MD Rabby Hossain",
				username: "md-rabby-hossain-808830",
				jobTitle: "Founder & CEO",
				company: "Abasan BD",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/95479cdb-12fd-4afd-a897-cf51e0e17c8a.jpeg`,
				location: "Bangladesh",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Md Zuhabul Islam",
				username: "md-zuhabul-islam-919065",
				jobTitle: "Chief Executive Officer",
				company: "Property Studio",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/ba7a9809-6ffc-4aaa-9e22-4f564bd5a664/founders/5e3fccba-da11-43fe-8c74-7d5bad60abe4.webp`,
				location: "Bangladesh",
				isFounder: true,
				isVerified: true,
			},
		],
		products: [
			{
				name: "Abasan BD",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/5429c562-a6c1-4bb7-9cc0-75a542ffdc50/logo/4c42602c-20ff-4dd2-968f-83c88d9bc4a2.png`,
				slug: "abasan-bd",
			},
			{
				name: "GEN Z Property Talks",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/6d7e8e0f-8e4e-4291-879d-d283ccc72591/logo/e11b5a3b-4fd2-4650-877b-12d19449c28b.png`,
				slug: "gen-z-property-talks",
			},
			{
				name: "Property Studio",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/ba7a9809-6ffc-4aaa-9e22-4f564bd5a664/logo/36a0e8de-26c6-4219-859d-ab4c6491cf74.webp`,
				slug: "property-studio",
			},
		],
	},
};

export const COUNTRY_LATAM_DATA: CountryContextType = {
	name: "LATAM",
	code: "LATAM",
	country: LATAM_COUNTRIES,

	meta: {
		name: {
			title: "LATAM",
			value: "LATAM",
			adjective: " Latin American",
		},
	},

	hero: {
		backgroundImage: "/images/communities/country-portal/bangladesh-header-banner.png",
		flagImage:
			"https://d2psdcplj93ozo.cloudfront.net/communities/5b606cbd-bcf5-47f2-b61d-8ec6cc022eee/logo-new-project-10--1753381232691.jpg",
		flagAlt: "LATAM",
		title: "Discover LATAM Proptech",
		screenReaderTitle: "Discover LATAM Proptech Products",
		description:
			"One stop shop to explore proptech technologies in LATAM. It covers products from proptech, construction tech, urban tech, clean tech etc.",
		welcomeMessage: "Welcome to Proptech LATAM",
	},

	ambassadors: [
		{
			image: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/b1932a47-74a5-492c-9df5-e40ad695f8b8.jpg`,
			name: "Oriel Esquivel",
			company: "Copilot Real Estate",
			profileLink: "https://www.proptechbuzz.com/user/oriel-esquivel-614200",
		},
	],

	vips: {
		title: "Our Premium VIPs from LATAM",
	},

	products: {
		perPage: 80,
	},

	stats: {
		products: "200+",
		members: "2500+",
		views: "124.4k",
		communities: "1200+",
		courses: "800+",
	},

	portals: {
		current: {
			labelText: "New",
			title: "LATAM Community",
			labelTextClassName: "bg-red-500",
			href: "/communities/latam/home",
			logo: "https://d2psdcplj93ozo.cloudfront.net/communities/5b606cbd-bcf5-47f2-b61d-8ec6cc022eee/logo-new-project-10--1753381232691.jpg",
		},
		others: [
			{
				labelText: "Hot",
				title: "PropTech Bangladesh",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-bangladesh/home",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/private-discussion-boards/proptech-bangladesh/proptech-bangladesh-logo.png`,
			},
			{
				labelText: "Hot",
				title: "PropTech India",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-india/home",
				logo: "/images/communities/country-portal/prop-tech-india.png",
			},
			{
				labelText: "Hot",
				title: "PropTech UAE",
				labelTextClassName: "bg-red-500",
				href: "/communities/proptech-uae/home",
				logo: "/images/communities/country-portal/uae-flag.png",
			},
			{
				labelText: "New",
				title: "PropTech Belgium",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-belgium.png",
			},
			{
				labelText: "New",
				title: "PropTech Austria",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-austria.png",
			},
			{
				labelText: "New",
				title: "PropTech South Africa",
				labelTextClassName: "bg-green-500",
				logo: "/images/communities/country-portal/prop-tech-south-africa.png",
			},
		],
	},

	featuredVideo: {
		thumbnailUrl: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX || ""}/`,
		thumbnailAlt: "LATAM Portal Launch",
		videoUrl: "https://www.youtube-nocookie.com/embed/XGWt3HRQ6-Y?autoplay=1&start=0&mute=1",
		videoTitle: "LATAM Portal Launch",
		text: "LATAM Portal Launch",
	},

	jobBoard: {
		title: "LATAM PropTech Jobs",
		jobs: [
			{
				logo: "https://media.licdn.com/dms/image/v2/C4E0BAQHw5F6CpY9-hQ/company-logo_100_100/company-logo_100_100/0/1663804182598?e=1756339200&v=beta&t=yaxmPzFpDSubdUvOAcJxvnftfykiqNfyLUBgP3ZZl5A",
				title: "Real Estate Acquisition Manager",
				location: "Latin America",
				duration: "Full Time",
				company: "Remote Latinos",
				isRemote: true,
				applyLink: "https://www.linkedin.com/jobs/view/4257628586",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://media.licdn.com/dms/image/v2/D4D0BAQFNIp2GFE64MA/company-logo_100_100/B4DZYBmseBHwAQ-/0/1743783643697/uptalent_io_logo?e=1756339200&v=beta&t=2H2uj2K2guf8LOG4GnS56aBj4qKN2PjPeBN7IeJchkM",
				title: "Remote Senior Analyst",
				location: "Brazil",
				duration: "Full Time",
				company: "Uptalent.io",
				isRemote: true,
				applyLink: "https://www.linkedin.com/jobs/view/4268615204",
				lastUpdated: "2 days ago",
			},
			{
				logo: "https://media.licdn.com/dms/image/v2/C560BAQHTvZwCx4p2Qg/company-logo_100_100/company-logo_100_100/0/1630640869849/amazon_logo?e=1756339200&v=beta&t=pUgkQO3FC1gL1s1SkI7hTP6v8g_wLQd029Xuehtji_I",
				title: "Senior Construction Manager",
				location: "Brazil",
				duration: "Full Time",
				company: "Amazon",
				isRemote: true,
				applyLink: "https://www.linkedin.com/jobs/view/4265167070/",
				lastUpdated: "1 day ago",
			},
		],
	},

	zeroStateSearch: [
		{
			title: "people",
			values: [
				{
					name: "Oriel Esquivel",
					link: "https://www.proptechbuzz.com/user/oriel-esquivel-614200",
				},
				{
					name: "Ronald Paixão",
					link: "https://www.proptechbuzz.com/user/ronald-paixo-254341",
				},
				{
					name: "Juan Amaya",
					link: "https://www.proptechbuzz.com/user/juan-amaya-657873",
				},
				{
					name: "Nicolas Maldonado",
					link: "https://www.proptechbuzz.com/user/nicolas-maldonado-791927",
				},
			],
		},
		{
			title: "products",
			values: [
				{
					name: "QuintoAndar",
					link: "https://www.proptechbuzz.com/companies/quintoandar-279757-5acd9915/products/quintoandar-765996",
				},
				{
					name: "Kenlo",
					link: "https://www.proptechbuzz.com/companies/kenlo-948321-1a65d62d/products/kenlo-513997",
				},
				{
					name: "Palomma",
					link: "https://www.proptechbuzz.com/companies/palomma-614472-b936749c/products/palomma-141672",
				},
			],
		},
		{
			title: "discussion",
			values: [
				{
					name: "BIENVENIDOS A LATAM ProptechBuzz!",
					link: "https://www.proptechbuzz.com/communities/latam/latam-general/bienvenidos-a-latam-proptechbuzz-836608",
				},
			],
		},
		{
			title: "community",
			values: [
				{
					name: "LATAM Community",
					link: "https://www.proptechbuzz.com/communities/latam",
				},
			],
		},
		{
			title: "event",
			values: [
				{
					name: "Expo CIHAC 2025",
					link: "https://www.expocihac.com/en/home.html",
				},
				{
					name: "Expo Nacional Ferretera",
					link: "https://www.expoferretera.com.mx/en-gb.html#/",
				},
				{
					name: "Real Estate Innovation Latam - REInn Edición Perú 2025",
					link: "https://lu.ma/ac64bcs0",
				},
			],
		},
		{
			title: "news",
			values: [
				{
					name: "Latin America Startup Sector Sees 26% Investment Jump",
					link: "https://www.proptechbuzz.com/news/latin-america-startup-investment-2024-growth",
				},
				{
					name: "Latin America's Construction Market Forecasts Modest Growth to USD 514.29 Billion by 2030",
					link: "https://www.proptechbuzz.com/news/latin-america-construction-market-growth",
				},
				{
					name: "Emerging Trends in Latin America’s Real Estate Market",
					link: "https://www.proptechbuzz.com/news/latin-america-real-estate-trends",
				},
			],
		},
	],

	popularTabs: {
		communities: [
			{
				labelText: "New",
				title: "LATAM Community",
				labelTextClassName: "bg-green-500",
				href: `${PRIVATE_DISCUSSION_BOARD_PATH}/latam`,
				logo: "https://d2psdcplj93ozo.cloudfront.net/communities/5b606cbd-bcf5-47f2-b61d-8ec6cc022eee/logo-new-project-10--1753381232691.jpg",
			},
		],
		people: [
			{
				name: "Oriel Esquivel",
				username: "oriel-esquivel-614200",
				jobTitle: "CTO",
				company: "Copilot Real Estate",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/b1932a47-74a5-492c-9df5-e40ad695f8b8.jpg`,
				location: "Mexico",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Ronald Paixão",
				username: "ronald-paixo-254341",
				jobTitle: "CEO - Presidente",
				company: "Copilot Real Estate",
				profileImage:
					"https://media.licdn.com/dms/image/v2/D4D03AQFqMwXnFxvNaw/profile-displayphoto-shrink_400_400/B4DZedPGWqG8Ak-/0/1750689671060?e=1756339200&v=beta&t=Slrat30Uwre0iqK6n3g-QJzoUbGqZBQLnOXYq-0kpwg",
				location: "Brazil",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Juan Amaya",
				username: "juan-amaya-657873",
				jobTitle: "CEO & Co - Founder",
				company: "Somos Inmobiliarios",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/e683ccc0-6780-4d57-8150-3e9519578042.jpeg`,
				location: "Argentina",
				isFounder: true,
				isVerified: true,
			},
			{
				name: "Nicolas Maldonado",
				username: "nicolas-maldonado-791927",
				jobTitle: "CPO",
				company: "Toperty",
				profileImage: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/profile-pictures/c8f3a474-db53-4963-b182-1f81a37e5fc3.jpg`,
				location: "Colombia",
				isFounder: true,
				isVerified: true,
			},
		],
		products: [
			{
				name: "QuintoAndar",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/products/2c07d0ac-f153-4ffb-9371-e084ce4c3dbe/uploads/productLogoLink/quintoandar-com-br-logo-1753357979627.jpeg`,
				slug: "quintoandar-765996",
			},
			{
				name: "Kenlo",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/products/8605e316-6523-4719-965a-e58f20def167/uploads/productLogoLink/kenlobr-logo-1753360193148.jpeg`,
				slug: "kenlo-513997",
			},
			{
				name: "Palomma",
				logo: `${process.env.NEXT_PUBLIC_ASSETS_BASE_PREFIX}/products/5ced759b-06eb-4131-93f7-27bd2b2f57f6/uploads/productLogoLink/palomma-logo-1753361257214.jpeg`,
				slug: "palomma-141672",
			},
		],
	},
};

const CountryContext = createContext<CountryContextType | undefined>(undefined);

export const CountryProvider = ({
	children,
	countryData,
}: {
	children: ReactNode;
	countryData?: CountryContextType;
}) => {
	const currCountryData = countryData || COUNTRY_UNITED_STATES_DATA;

	return <CountryContext.Provider value={currCountryData}>{children}</CountryContext.Provider>;
};

// Hook for consuming the context
export const useCountryContext = () => {
	const context = useContext(CountryContext);
	if (!context) {
		throw new Error("useCountryContext must be used within a CountryProvider");
	}
	return context;
};
